import SEO from "@/components/SEO";
import Reveal, { Eyebrow } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import ClientAccess from "@/components/ClientAccess";
import { SITE } from "@/config/site";
import { MapleMark } from "@/components/MapleMark";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact Postings Inc. | Get Started — Bookkeeping Newmarket, Ontario"
        description="Tell us what you need help with. Bookkeeping, payroll, QuickBooks support, GST/HST and more for small and medium-sized Canadian businesses. 17665 Leslie Street Unit 35, Newmarket, ON. 289-464-1001."
      />

      <section className="ledger-lines relative overflow-hidden pt-32 pb-10 sm:pt-40">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Get Started</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 max-w-3xl font-cabinet text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-6xl">
              Let&apos;s Talk About <span className="text-accent">Your Business.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
              Tell us what you need help with and we&apos;ll connect you with the right member
              of the Postings team.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 pt-6 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7" data-testid="contact-form-section">
            <ContactForm />
          </div>

          <div className="space-y-6 lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-ink/5 bg-white p-8 shadow-subtle" data-testid="contact-info-card">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                  Contact Information
                </p>
                <h2 className="mt-2 font-cabinet text-2xl font-bold">Postings Inc.</h2>
                <address className="mt-4 space-y-1 not-italic text-slate-600">
                  <p>{SITE.address.street}</p>
                  <p>
                    {SITE.address.city}, {SITE.address.province}
                  </p>
                </address>
                <div className="mt-5 space-y-3 text-slate-800">
                  <a
                    href={SITE.phoneHref}
                    data-testid="contact-info-phone"
                    className="flex items-center gap-3 font-medium transition-colors hover:text-brand"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand/5 text-brand">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    </span>
                    {SITE.phoneDisplay}
                  </a>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-info-email"
                    className="flex items-center gap-3 font-medium transition-colors hover:text-brand"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand/5 text-brand">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    </span>
                    {SITE.email}
                  </a>
                </div>
                <div className="mt-6 border-t border-ink/5 pt-5">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                    <span aria-hidden="true">Office Hours</span>
                  </p>
                  <dl className="mt-3 space-y-2 text-sm">
                    {SITE.hours.map((h) => (
                      <div key={h.days} className="flex items-center justify-between gap-4">
                        <dt className="text-slate-500">{h.days}</dt>
                        <dd className="font-medium text-slate-800">{h.time}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={SITE.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-booking-button"
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[rgb(0_40_140)]"
                  >
                    Book a Consultation
                  </a>
                  <a
                    href={SITE.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-maps-directions"
                    className="inline-flex items-center gap-2 rounded-full border border-ink/10 px-5 py-2.5 text-sm font-semibold transition-colors hover:border-brand hover:text-brand"
                  >
                    Get Directions
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
                  </a>
                </div>
                <p className="mt-5 flex items-center gap-2 text-xs text-slate-400">
                  <MapleMark className="h-3.5 w-3.5 text-accent" />
                  Proudly serving {SITE.regions}
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <ClientAccess />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
